import React, { useState, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import { Volume2, Lightbulb, Monitor, Video, Phone, Mail, MapPin, Clock, Camera, Zap, Headphones, Radio, Home as HomeIcon, Building2, Landmark, Gem, Drama, Tent, Ship, Wheat } from 'lucide-react';

const Home = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [waveformBars, setWaveformBars] = useState([]);
  const [counters, setCounters] = useState({ years: 0, events: 0, clients: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [activeService, setActiveService] = useState(0);
  const [activeVenue, setActiveVenue] = useState(null);
  const [activeProject, setActiveProject] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoVolume, setVideoVolume] = useState(0.7);
  const [videoVisible, setVideoVisible] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const statsRef = useRef(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const lenisRef = useRef(null);
  const parallaxRefs = useRef([]);

  // Video visibility observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVideoVisible(entry.isIntersecting);
          if (videoRef.current) {
            if (entry.isIntersecting) {
              // Try to play - may fail due to autoplay policy
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  // Autoplay prevented - video will show "Enable Sound" overlay
                });
              }
            } else {
              videoRef.current.pause();
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    if (videoSectionRef.current) {
      observer.observe(videoSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        smoothWheel: true,
      });
      lenisRef.current = lenis;
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
      return () => lenis.destroy();
    }
  }, [isMobile]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const bars = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      height: Math.random() * 80 + 20,
      delay: i * 0.05,
      duration: Math.random() * 0.5 + 0.5
    }));
    setWaveformBars(bars);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (currentScrollY / totalHeight) * 100;
      
      setScrollY(currentScrollY);
      setScrollProgress(progress);
      setShowBackToTop(currentScrollY > 300);
      
      if (!isMobile) {
        parallaxRefs.current.forEach((el, index) => {
          if (el) {
            const speed = (index + 1) * 0.05;
            el.style.transform = `translateY(${currentScrollY * speed}px)`;
          }
        });
      }
      
      const sections = document.querySelectorAll('section[id]');
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (currentScrollY >= sectionTop - 100) {
          setActiveTab(section.id);
        }
      });
    };

    const handleMouseMove = (e) => {
      if (!isMobile) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setStatsVisible(true);
      });
    };

    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.2 });
    if (statsRef.current) observer.observe(statsRef.current);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
    };
  }, [isMobile]);

  useEffect(() => {
    if (statsVisible) {
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setCounters({
          years: Math.round(40 * easeOut),
          events: Math.round(3000 * easeOut),
          clients: Math.round(2000 * easeOut)
        });
        if (step >= steps) clearInterval(timer);
      }, interval);
      return () => clearInterval(timer);
    }
  }, [statsVisible]);

  useEffect(() => {
    const interval = setInterval(() => setActiveService(prev => (prev + 1) % 6), 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleVideoMute = () => {
    if (videoRef.current) {
      const newMutedState = !videoMuted;
      videoRef.current.muted = newMutedState;
      setVideoMuted(newMutedState);
      if (!newMutedState) {
        setUserInteracted(true);
      }
    }
  };

  const enableSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      setVideoMuted(false);
      setUserInteracted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVideoVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setProjectModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectModal = () => {
    setProjectModalOpen(false);
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      if (lenisRef.current && !isMobile) {
        lenisRef.current.scrollTo(section, { offset: -70 });
      } else {
        window.scrollTo({ top: section.offsetTop - 70, behavior: 'smooth' });
      }
      setActiveTab(sectionId);
      setIsMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    if (lenisRef.current && !isMobile) {
      lenisRef.current.scrollTo(0);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const navItems = ['home', 'about', 'services', 'work', 'gallery', 'venues', 'contact'];

  const venues = [
    { name: 'Viramgam', x: 35, y: 35, events: '500+', desc: 'Home base', Icon: HomeIcon },
    { name: 'Ahmedabad', x: 55, y: 40, events: '1200+', desc: 'Corporate hub', Icon: Building2 },
    { name: 'Gandhinagar', x: 58, y: 32, events: '400+', desc: 'Government', Icon: Landmark },
    { name: 'Surat', x: 48, y: 75, events: '350+', desc: 'Diamond city', Icon: Gem },
    { name: 'Vadodara', x: 65, y: 50, events: '280+', desc: 'Cultural capital', Icon: Drama },
    { name: 'Rajkot', x: 25, y: 45, events: '200+', desc: 'Navratri hub', Icon: Tent },
    { name: 'Bhavnagar', x: 40, y: 70, events: '150+', desc: 'Coastal events', Icon: Ship },
    { name: 'Mehsana', x: 45, y: 20, events: '180+', desc: 'Mega events', Icon: Wheat },
  ];

  const allImages = [
    { img: 'amit-shah-roadshow-ahmedabad.png', title: 'Political Roadshow', cat: 'Government' },
    { img: 'audio-engineer.png', title: 'Audio Engineering', cat: 'Behind Scenes' },
    { img: 'dj-viola-international.png', title: 'DJ Viola', cat: 'International' },
    { img: 'garba-shows-live-sound.png', title: 'Garba Festival', cat: 'Cultural' },
    { img: 'gujarat-vandebharat-train-opening.png', title: 'Vande Bharat', cat: 'Government' },
    { img: 'gujarati-singer-1.png', title: 'Singer Live', cat: 'Concert' },
    { img: 'gujarati-singer.png', title: 'Vocal Performance', cat: 'Live Music' },
    { img: 'international-dj-sound-reinforcement.png', title: 'DJ Setup', cat: 'DJ Night' },
    { img: 'ishani-dave-artist.png', title: 'Ishani Dave', cat: 'Celebrity' },
    { img: 'live-event-performance-1.png', title: 'Stage Show', cat: 'Entertainment' },
    { img: 'live-event-performance-2.png', title: 'Live Concert', cat: 'Concert' },
    { img: 'live-event-performance-3.png', title: 'Festival', cat: 'Festival' },
    { img: 'live-event-performance-4.png', title: 'Grand Event', cat: 'Mega Event' },
    { img: 'live-sound-reinforcement.png', title: 'Sound Setup', cat: 'Technical' },
    { img: 'malhar-thakar-gujarati-artist.png', title: 'Malhar Thakar', cat: 'Celebrity' },
    { img: 'sound-lights-led-production-new-year.png', title: 'New Year Bash', cat: 'Party' },
    { img: 'sound-lights-trussing-1.png', title: 'Truss Setup', cat: 'Technical' },
    { img: 'sound-lights-trussing-2.png', title: 'Stage Design', cat: 'Design' },
    { img: 'sound-trussing.png', title: 'Truss Structure', cat: 'Infrastructure' },
    { img: 'sufi-gazal-night.png', title: 'Sufi Night', cat: 'Cultural' },
    { img: 'engineers-practice-workshops.jpg', title: 'Training', cat: 'Workshop' },
  ];

  const services = [
    { title: 'Premium Sound', Icon: Volume2, price: 'From ₹35k', img: 'audio-engineer.png', specs: ['50,000W', 'Line Arrays', '32-Ch', 'Wireless'] },
    { title: 'Pro Lighting', Icon: Lightbulb, price: 'From ₹25k', img: 'sound-lights-trussing-1.png', specs: ['Moving Heads', 'LED Walls', 'Lasers', 'DMX'] },
    { title: 'LED Video Walls', Icon: Monitor, price: 'From ₹45k', img: 'sound-lights-led-production-new-year.png', specs: ['P2.5 Panels', '40ft', 'Live Cam', '4K'] },
    { title: 'Live Streaming', Icon: Radio, price: 'From ₹20k', img: 'live-event-performance-4.png', specs: ['Multi-Cam', 'HD', 'YouTube', 'Real-time'] },
    { title: 'DJ & Stage', Icon: Headphones, price: 'From ₹30k', img: 'international-dj-sound-reinforcement.png', specs: ['Pioneer DJ', 'Stage', 'Truss', 'Monitors'] },
    { title: 'Full Package', Icon: Video, price: 'From ₹1.5L', img: 'live-event-performance-1.png', specs: ['Full Setup', '8 Crew', 'Mgmt', '24/7'] },
  ];

  const projects = [
    { 
      id: '01', 
      title: 'Navratri Celebrations', 
      subtitle: 'Annual Garba Festival', 
      location: 'Viramgam, Ahmedabad', 
      desc: 'Flagship event with 50,000W JBL arrays for 10,000+ crowds nightly.', 
      year: '1985-Present', 
      stats: '3000+', 
      tags: ['Cultural', 'Mega Event'], 
      featured: true, 
      img: 'garba-shows-live-sound.png',
      details: {
        challenge: 'Providing crystal-clear audio for massive crowds of 10,000+ people dancing simultaneously, while maintaining consistent sound coverage across large open grounds.',
        solution: 'Deployed a massive 50,000W JBL VTX line array system with 24 subwoofers for deep bass response. Installed distributed delay towers to ensure uniform sound throughout the venue.',
        equipment: ['JBL VTX V25 Line Arrays', '48x JBL Subwoofers', 'Crown I-Tech HD Amplifiers', 'Soundcraft Vi7000 Console', 'Shure Wireless Systems'],
        results: 'Successfully powered 3,000+ nights of Garba celebrations over 40 years. The sound system maintains clarity even at 120dB, ensuring every beat is felt by the crowd.',
        testimonial: 'Amit Sound has been our trusted partner for Navratri since 1985. Their consistency and professionalism are unmatched. - Jayesh Patel, Event Organizer'
      }
    },
    { 
      id: '02', 
      title: 'Government Events', 
      subtitle: 'State Ceremonies', 
      location: 'Gujarat Statewide', 
      desc: 'Vande Bharat inaugurations with 4K broadcast.', 
      year: '2018-Present', 
      stats: '200+', 
      tags: ['Government', 'Official'], 
      featured: false, 
      img: 'gujarat-vandebharat-train-opening.png',
      details: {
        challenge: 'Delivering flawless audio for high-profile government events with live television broadcast, requiring zero tolerance for technical failures.',
        solution: 'Implemented redundant audio systems with backup consoles, wireless mics, and power supplies. Provided real-time audio feeds for Doordarshan and news channels.',
        equipment: ['Martin Audio MLA Arrays', 'Digital Mixing Consoles', ' redundant Backup Systems', 'Broadcast Quality Microphones', 'Fiber Optic Audio Distribution'],
        results: 'Successfully handled 200+ government events including Vande Bharat train inaugurations, CM visits, and state ceremonies with 100% uptime.',
        testimonial: 'Amit Sound delivers the reliability we need for official state functions. Their team is always prepared. - Govt. of Gujarat Protocol Office'
      }
    },
    { 
      id: '03', 
      title: 'Celebrity Shows', 
      subtitle: 'Artist Production', 
      location: 'Multiple Venues', 
      desc: 'Premium production for Malhar Thakar, Ishani Dave.', 
      year: '2000-Present', 
      stats: '500+', 
      tags: ['Celebrity', 'Premium'], 
      featured: false, 
      img: 'malhar-thakar-gujarati-artist.png',
      details: {
        challenge: 'Meeting the demanding technical requirements of top Gujarati celebrities who expect concert-grade audio quality at every show.',
        solution: 'Partnered with artists to understand their specific requirements. Deployed rider-compliant systems with premium monitoring and front-of-house setups.',
        equipment: ['d&b Audiotechnik Arrays', 'Avid S6L Consoles', 'In-Ear Monitoring Systems', 'Premium Backline Gear', 'Custom Stage Design'],
        results: 'Provided production for 500+ celebrity shows including Malhar Thakar, Ishani Dave, Geeta Rabari, and many more. Artists consistently request our services.',
        testimonial: 'Amit Sound understands what artists need. Their monitor mix is always perfect. - Malhar Thakar, Gujarati Film Star'
      }
    },
    { 
      id: '04', 
      title: 'Political Roadshows', 
      subtitle: 'Campaign Production', 
      location: 'Ahmedabad', 
      desc: 'Major roadshows with mobile arrays and drone coverage.', 
      year: 'Various', 
      stats: '50+', 
      tags: ['Political', 'Roadshow'], 
      featured: false, 
      img: 'amit-shah-roadshow-ahmedabad.png',
      details: {
        challenge: 'Setting up professional audio systems rapidly at multiple locations during fast-moving political roadshows, often with only 30 minutes setup time.',
        solution: 'Developed rapid-deployment mobile systems with pre-configured racks. Deployed trained crews at each stop for quick load-in and load-out.',
        equipment: ['Mobile PA Trailers', 'Quick-Rig Line Arrays', 'Wireless PA Systems', 'Portable Power Generators', 'Drone Integration'],
        results: 'Successfully managed 50+ major political roadshows including national-level campaigns. Handled crowds up to 100,000 people with clear speech intelligibility.',
        testimonial: 'Their mobile setup is incredibly efficient. We never worry about audio, even with tight schedules. - Political Campaign Manager'
      }
    },
    { 
      id: '05', 
      title: 'Cultural Nights', 
      subtitle: 'Sufi & Traditional', 
      location: 'All Gujarat', 
      desc: 'Authentic sufi gazal nights with custom acoustics.', 
      year: 'Various', 
      stats: '150+', 
      tags: ['Cultural', 'Sufi'], 
      featured: false, 
      img: 'sufi-gazal-night.png',
      details: {
        challenge: 'Creating intimate acoustic environments for Sufi and Gazal performances where subtlety and nuance are more important than sheer volume.',
        solution: 'Custom acoustic treatments for each venue. Deployed high-fidelity point-source systems optimized for vocal clarity and instrument detail.',
        equipment: ['L-Acoustics Kara Systems', 'Premium Vocal Microphones', 'Acoustic Treatment Panels', 'Subtle LED Lighting', 'Traditional Instrument Pickups'],
        results: 'Created the perfect ambiance for 150+ cultural nights. Audiences experience every emotional nuance of the performances with crystal clarity.',
        testimonial: 'For Sufi music, you need more than loud speakers - you need soul. Amit Sound delivers that. - Firoz Bard, Event Curator'
      }
    },
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden font-sans selection:bg-red-500 selection:text-white transition-colors duration-300 ${darkMode ? 'bg-[#030303] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Page Load */}
      {isLoading && (
        <div className="fixed inset-0 z-[100] bg-[#030303] flex items-center justify-center">
          <div className="text-center px-4">
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6">
              <img src={require('../assets/images/logo.jpg')} alt="Amit Sound & Production Logo" className="w-full h-full object-contain rounded-xl" />
              <div className="absolute inset-0 border-2 border-red-500/30 rounded-xl animate-ping"></div>
            </div>
            <h2 className="text-xl sm:text-3xl font-black tracking-wider"><span className="text-white">AMIT</span><span className="text-red-500">SOUND</span></h2>
          </div>
        </div>
      )}

      {/* Cursor */}
      {!isMobile && (
        <div className="fixed w-3 h-3 bg-red-500 rounded-full pointer-events-none z-[99] transition-transform duration-75 hidden lg:block" style={{ left: mousePosition.x - 6, top: mousePosition.y - 6 }} />
      )}

      {/* Scroll Progress */}
      <div className="fixed top-0 left-0 w-full h-0.5 z-[60]">
        <div className="h-full bg-gradient-to-r from-red-600 to-red-400" style={{ width: `${scrollProgress}%` }}></div>
      </div>

      {/* Animated Gradient Waves */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 opacity-[0.03]" style={{ background: 'radial-gradient(ellipse at center, rgba(220, 38, 38, 0.4) 0%, transparent 50%)', animation: 'wave1 20s ease-in-out infinite' }}></div>
        <div className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 opacity-[0.02]" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(220, 38, 38, 0.5) 0%, transparent 40%)', animation: 'wave2 25s ease-in-out infinite' }}></div>
        <div className="absolute w-[150%] h-[150%] bottom-0 right-0 opacity-[0.02]" style={{ background: 'radial-gradient(ellipse at 80% 80%, rgba(220, 38, 38, 0.6) 0%, transparent 50%)', animation: 'wave3 30s ease-in-out infinite' }}></div>
      </div>

      {/* Background Grid */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(220, 38, 38, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(220, 38, 38, 0.02) 1px, transparent 1px)`, backgroundSize: isMobile ? '30px 30px' : '50px 50px' }}></div>
      </div>

      {/* Mouse Gradient */}
      {!isMobile && (
        <div className="fixed inset-0 pointer-events-none z-0 transition-all duration-700" style={{ background: `radial-gradient(circle 400px at ${mousePosition.x}px ${mousePosition.y}px, rgba(220, 38, 38, 0.08), transparent)` }}></div>
      )}

      {/* Back to Top */}
      <button onClick={scrollToTop} className={`fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 bg-red-500/90 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-lg transition-all ${showBackToTop ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
      </button>

      {/* Mobile Call */}
      <a href="tel:9624569200" className="fixed bottom-4 left-4 z-50 bg-green-500 text-white px-4 py-3 rounded-full flex items-center space-x-2 shadow-lg lg:hidden">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
        <span className="font-bold text-sm">Call</span>
      </a>

      {/* ENHANCED NAVBAR */}
      <nav className={`fixed w-full z-40 transition-all duration-500 ${scrollY > 50 ? (darkMode ? 'bg-[#030303]/95 backdrop-blur-xl border-b border-red-500/20 shadow-lg shadow-red-500/5' : 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-lg') : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer group" onClick={() => scrollToSection('home')}>
            <div className="relative">
              <img src={require('../assets/images/logo.jpg')} alt="Amit Sound & Production Logo" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover border-2 border-red-500/40 shadow-lg shadow-red-500/20 transition-all group-hover:border-red-500 group-hover:shadow-red-500/40" />
              <div className="absolute inset-0 bg-red-500/10 rounded-lg animate-pulse"></div>
            </div>
            <div className="hidden sm:block">
              <div className="flex items-baseline space-x-1">
                <span className="text-xl sm:text-2xl font-black tracking-wider">AMIT</span>
                <span className="text-xl sm:text-2xl font-black tracking-wider text-red-500">SOUND</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-[1px] w-4 bg-red-500/60"></div>
                <span className={`text-[10px] tracking-widest uppercase ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Est. 1985</span>
              </div>
            </div>
          </div>

          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <button key={item} onClick={() => scrollToSection(item)} className={`capitalize text-xs tracking-wider transition-all duration-300 px-4 py-2 rounded-full relative overflow-hidden group ${activeTab === item ? (darkMode ? 'text-white bg-red-500/20' : 'text-red-600 bg-red-50') : (darkMode ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-red-600 hover:bg-gray-100')}`}>
                <span className="relative z-10">{item}</span>
                {activeTab === item && <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-transparent"></div>}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-500 group-hover:w-1/2 transition-all duration-300"></div>
              </button>
            ))}
          </div>

          <a href="tel:9624569200" className="hidden lg:flex items-center space-x-3 bg-gradient-to-r from-red-600 to-red-500 text-white px-5 py-3 rounded-full hover:shadow-lg hover:shadow-red-500/30 hover:scale-105 transition-all text-sm font-bold group">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-[10px] text-white/70">24/7 Available</span>
              <span>9624569200</span>
            </div>
          </a>

          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme} 
            className={`hidden lg:flex w-10 h-10 rounded-full items-center justify-center border transition-all ${darkMode ? 'border-white/20 hover:border-red-500 hover:bg-red-500/10' : 'border-gray-400 hover:border-red-500 hover:bg-red-500/10'}`}
            aria-label="Toggle theme"
          >
            {darkMode ? (
              <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <button className="lg:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <div className="relative w-6 h-6">
              <span className={`absolute left-0 w-6 h-0.5 transition-all ${darkMode ? 'bg-white' : 'bg-gray-900'} ${isMenuOpen ? 'top-3 rotate-45' : 'top-1'}`}></span>
              <span className={`absolute left-0 top-3 w-6 h-0.5 transition-all ${darkMode ? 'bg-white' : 'bg-gray-900'} ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`absolute left-0 w-6 h-0.5 transition-all ${darkMode ? 'bg-white' : 'bg-gray-900'} ${isMenuOpen ? 'top-3 -rotate-45' : 'top-5'}`}></span>
            </div>
          </button>
        </div>

        {isMenuOpen && (
          <div className={`lg:hidden backdrop-blur-xl border-t border-red-500/10 ${darkMode ? 'bg-[#030303]/98' : 'bg-white/98'}`}>
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-1">
              {navItems.map((item) => (
                <button key={item} onClick={() => scrollToSection(item)} className={`capitalize text-left py-3 px-4 text-base tracking-wider rounded-lg transition-colors ${activeTab === item ? 'text-red-500 bg-red-500/10' : (darkMode ? 'text-white/70 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100')}`}>
                  {item}
                </button>
              ))}
              <div className="pt-4 flex space-x-3">
                <a href="tel:9624569200" className="flex-1 bg-red-500 text-white py-3 rounded-lg text-center font-bold">Call Now</a>
                <a href="https://wa.me/919624569200" className="flex-1 bg-green-500 text-white py-3 rounded-lg text-center font-bold">WhatsApp</a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ENHANCED HERO */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-b from-[#030303] via-[#050505] to-[#030303]' : 'bg-gradient-to-b from-gray-100 via-white to-gray-100'}`}></div>
          <div className={`absolute inset-0 ${darkMode ? 'bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.15),transparent_60%)]' : 'bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.08),transparent_60%)]'}`}></div>
        </div>

        {/* Logo Watermark */}
        <div className={`absolute inset-0 z-0 flex items-center justify-center pointer-events-none ${darkMode ? 'opacity-[0.03]' : 'opacity-[0.04]'}`}>
          <div className="relative w-[500px] h-[500px]">
            <img 
              src={require('../assets/images/logo.jpg')} 
              alt="Amit Sound & Production Logo" 
              className="w-full h-full object-contain grayscale blur-md"
              style={{ filter: 'grayscale(100%) blur(12px)' }}
            />
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-gray-100/80"></div>
          </div>
        </div>

        {/* FLOATING IMAGES WITH SHADOWS */}
        {!isMobile && (
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <div className="absolute left-[2%] top-[20%] animate-float-slow" style={{ transform: `translateY(${scrollY * 0.05}px)` }}>
              <div className="relative">
                <div className="absolute -bottom-8 -right-8 w-32 h-44 bg-red-500/20 rounded-xl blur-xl"></div>
                <div className="w-32 sm:w-48 h-44 sm:h-64 rounded-xl overflow-hidden border border-red-500/20 shadow-2xl shadow-red-500/10">
                  <img src={require('../assets/images/audio-engineer.png')} alt="Professional Audio Engineer at Sound Mixing Console" className="w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/80 to-transparent"></div>
                </div>
              </div>
            </div>

            <div className="absolute right-[3%] top-[15%] animate-float-medium" style={{ transform: `translateY(${scrollY * -0.08}px)` }}>
              <div className="relative">
                <div className="absolute -bottom-8 -left-8 w-28 h-40 bg-red-500/20 rounded-xl blur-xl"></div>
                <div className="w-28 sm:w-40 h-40 sm:h-56 rounded-xl overflow-hidden border border-red-500/20 shadow-2xl shadow-red-500/10">
                  <img src={require('../assets/images/dj-viola-international.png')} alt="International DJ Performing Live with Professional Sound System" className="w-full h-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/80 to-transparent"></div>
                </div>
              </div>
            </div>

            <div className="absolute left-[8%] bottom-[25%] animate-float-fast" style={{ transform: `translateY(${scrollY * 0.03}px) rotate(-5deg)` }}>
              <div className="relative">
                <div className="absolute -bottom-6 -right-6 w-24 h-32 bg-red-500/15 rounded-xl blur-lg"></div>
                <div className="w-24 sm:w-32 h-32 sm:h-44 rounded-xl overflow-hidden border border-white/10 shadow-xl">
                  <img src={require('../assets/images/sound-lights-trussing-1.png')} alt="Professional Stage Lighting and Sound Truss Setup" className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-[#030303]/60"></div>
                </div>
              </div>
            </div>

            <div className="absolute right-[10%] bottom-[30%] animate-float-slow" style={{ transform: `translateY(${scrollY * -0.04}px) rotate(5deg)` }}>
              <div className="relative">
                <div className="absolute -bottom-6 -left-6 w-20 h-28 bg-red-500/15 rounded-xl blur-lg"></div>
                <div className="w-20 sm:w-28 h-28 sm:h-40 rounded-xl overflow-hidden border border-white/10 shadow-xl">
                  <img src={require('../assets/images/garba-shows-live-sound.png')} alt="Live Garba Festival with Professional Sound Reinforcement" className="w-full h-full object-cover opacity-50" />
                  <div className="absolute inset-0 bg-gradient-to-bl from-transparent to-[#030303]/60"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Waveform */}
        { /* ... */ }
        <div className="absolute bottom-0 left-0 w-full h-32 sm:h-48 flex items-end justify-center space-x-[2px] sm:space-x-[3px] opacity-20 z-0 px-2">
          {(isMobile ? waveformBars.slice(0, 20) : waveformBars).map(bar => (
            <div key={bar.id} className="bg-gradient-to-t from-red-600 to-red-400 w-[2px] sm:w-[3px] rounded-t-full" style={{ height: `${bar.height}%`, animation: `waveform ${bar.duration}s ease-in-out infinite alternate`, animationDelay: `${bar.delay}s` }} />
          ))}
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 sm:px-6 z-10 py-8 sm:py-0">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 mb-4 sm:mb-6 px-4 py-2 backdrop-blur-sm rounded-full border border-white/10 ${darkMode ? 'bg-white/5' : 'bg-white/80'}">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Since 1985</span>
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse delay-75"></span>
            </div>
            
            <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black leading-[0.85] sm:leading-[0.8] mb-4 sm:mb-6">
              <span className={`block drop-shadow-2xl ${darkMode ? 'text-white' : 'text-gray-900'}`}>AMIT</span>
              <span className="block bg-gradient-to-r from-red-600 via-red-500 to-red-400 bg-clip-text text-transparent drop-shadow-lg">SOUND</span>
            </h1>

            <p className={`text-lg sm:text-2xl font-light mb-2 tracking-wider uppercase ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>& Production</p>
            
            <div className="flex items-center justify-center space-x-4 mb-6 sm:mb-10">
              <div className="h-[1px] w-12 sm:w-20 bg-gradient-to-r from-transparent to-red-500/50"></div>
              <p className={`text-xs sm:text-sm max-w-lg ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Gujarat's premier sound & lighting production company delivering exceptional audio experiences for concerts, corporate events, weddings, and government functions since 1985</p>
              <div className="h-[1px] w-12 sm:w-20 bg-gradient-to-l from-transparent to-red-500/50"></div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4 sm:px-0 mb-8 sm:mb-12">
              <button onClick={() => scrollToSection('work')} className="group bg-gradient-to-r from-red-600 to-red-500 text-white px-8 sm:px-10 py-4 rounded-full font-bold text-sm tracking-wider hover:shadow-xl hover:shadow-red-500/30 hover:scale-105 transition-all flex items-center justify-center space-x-2">
                <span>VIEW WORK</span>
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </button>
              <button onClick={() => scrollToSection('contact')} className={`group backdrop-blur-sm border px-8 sm:px-10 py-4 rounded-full font-bold text-sm tracking-wider transition-all flex items-center justify-center space-x-2 ${darkMode ? 'bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-red-500/50' : 'bg-gray-100 border-gray-300 text-gray-900 hover:bg-white hover:border-red-500'}`}>
                <span>CONTACT US</span>
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              </button>
            </div>

            <div className="flex justify-center gap-8 sm:gap-16">
              {[{ v: '40+', l: 'Years' }, { v: '3000+', l: 'Events' }, { v: '2000+', l: 'Clients' }].map((stat, i) => (
                <div key={i} className="text-center group cursor-default">
                  <div className="text-2xl sm:text-4xl font-black bg-gradient-to-b from-red-500 to-red-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform">{stat.v}</div>
                  <div className={`text-xs sm:text-sm tracking-wider uppercase mt-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{stat.l}</div>
                </div>
              ))}
            </div>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 animate-bounce">
              <span className={`text-xs tracking-widest uppercase ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>Scroll</span>
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section id="about" className={`relative py-12 sm:py-20 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Subtle Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] ${darkMode ? 'bg-red-500/5' : 'bg-red-500/8'}`}></div>
          <div className={`absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[100px] ${darkMode ? 'bg-red-600/5' : 'bg-red-600/6'}`}></div>
        </div>
        {/* Logo Watermark */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[400px] h-[400px] opacity-[0.02] pointer-events-none">
          <img src={require('../assets/images/logo.jpg')} alt="" className="w-full h-full object-contain grayscale" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-[2px] w-8 bg-red-500"></div>
                <span className="text-red-500 text-xs tracking-[0.2em] uppercase">About Us</span>
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight">
                CRAFTING<br />
                <span className="text-red-500">UNFORGETTABLE</span><br />
                <span className={darkMode ? 'text-white/20' : 'text-gray-300'}>EVENTS SINCE 1985</span>
              </h2>
              <p className={`text-base sm:text-lg leading-relaxed mb-4 ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>
                For over four decades, Amit Sound & Production has been the heartbeat of Gujarat's most extraordinary events. From intimate wedding celebrations to massive government ceremonies hosting 50,000+ attendees, we've powered thousands of successful events across the state.
              </p>
              <p className={`text-sm sm:text-base leading-relaxed mb-6 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>
                Our expert team specializes in delivering crystal-clear audio, stunning lighting designs, and seamless event production. With state-of-the-art equipment including 50,000W JBL line arrays, intelligent moving heads, and massive LED video walls, we transform ordinary venues into extraordinary experiences.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['Corporate Events', 'Weddings', 'Concerts', 'Government Functions', 'Cultural Festivals', 'Political Roadshows'].map((tag, i) => (
                  <span key={i} className="text-xs bg-red-500/10 text-red-400 px-3 py-1.5 rounded-full border border-red-500/20">{tag}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[{ icon: Volume2, t: 'Premium Audio', d: '50,000W+ Systems' }, { icon: Lightbulb, t: 'Pro Lighting', d: 'Intelligent FX' }, { icon: Monitor, t: 'LED Walls', d: '40ft Displays' }, { icon: Video, t: 'Live Stream', d: 'HD Broadcast' }].map((f, i) => (
                  <div key={i} className={`rounded-xl p-4 hover:border-red-500/30 transition-colors group ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
                    <div className="text-2xl mb-2 group-hover:scale-110 transition-transform text-red-500"><f.icon className="w-6 h-6" /></div>
                    <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{f.t}</h3>
                    <p className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{f.d}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-red-500/20">
                <img src={require('../assets/images/live-event-performance-1.png')} alt="Live Stage Performance with Professional Sound and Lighting" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 bg-[#0a0a0a]/90 backdrop-blur border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-500 text-xs font-medium">Trusted By Government of Gujarat</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className={`relative py-10 sm:py-16 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 left-1/4 w-[700px] h-[700px] rounded-full blur-[180px] ${darkMode ? 'bg-red-500/8' : 'bg-red-500/12'}`}></div>
          <div className={`absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[140px] ${darkMode ? 'bg-red-600/6' : 'bg-red-600/10'}`}></div>
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <span className="text-red-500 text-xs tracking-[0.3em] uppercase font-medium">Why Choose Us</span>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black mt-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Sound That <span className="text-red-500">Elevates</span> Every Event
            </h2>
            <p className={`max-w-2xl mx-auto mt-4 text-sm sm:text-base ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
              Leading sound equipment and service provider, delivering high-quality audio solutions for events of all scales
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                title: 'Premium Sound Equipment', 
                desc: 'High-performance sound systems including line arrays, professional mixers, wireless microphones, stage monitors, and powerful amplifiers. We use industry-leading brands like JBL, Martin Audio, and Yamaha to ensure exceptional sound quality for events of any size.',
                icon: Volume2 
              },
              { 
                title: 'Expert Sound Services', 
                desc: 'Complete event audio solutions including professional setup, live mixing by certified engineers, acoustics management, and on-site technical support. Our experienced team ensures flawless sound delivery from setup to show completion.',
                icon: Headphones 
              },
              { 
                title: '40+ Years Experience', 
                desc: 'Since 1985, we have powered thousands of successful events across Gujarat. From intimate weddings to massive political roadshows with 50,000+ attendees, our expertise ensures your event sounds perfect every time.',
                icon: Zap 
              }
            ].map((item, i) => (
              <div key={i} className={`group bg-gradient-to-br rounded-2xl p-6 hover:border-red-500/30 transition-all border ${darkMode ? 'from-white/[0.03] to-transparent border-white/10' : 'from-white to-gray-50 border-gray-200'}`}>
                <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-500/20 transition-colors">
                  <item.icon className="w-7 h-7 text-red-500" />
                </div>
                <h3 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Events Completed', value: '3000+' },
              { label: 'Happy Clients', value: '2000+' },
              { label: 'Cities Covered', value: '8+' },
              { label: 'Team Members', value: '25+' }
            ].map((stat, i) => (
              <div key={i} className={`rounded-xl p-4 border ${darkMode ? 'bg-white/[0.02] border-white/5' : 'bg-white border-gray-200'}`}>
                <div className="text-2xl sm:text-3xl font-black text-red-500">{stat.value}</div>
                <div className={`text-xs mt-1 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO SHOWCASE SECTION */}
      <section ref={videoSectionRef} id="video" className={`relative py-10 sm:py-16 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[160px] ${darkMode ? 'bg-red-600/8' : 'bg-red-600/12'}`}></div>
          <div className={`absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[130px] ${darkMode ? 'bg-red-500/6' : 'bg-red-500/10'}`}></div>
          <div className={`absolute top-1/3 right-1/3 w-[300px] h-[300px] rounded-full blur-[90px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Experience</span>
            <h2 className={`text-3xl sm:text-5xl font-black mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>SEE US IN ACTION</h2>
            <p className={`mt-3 text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Watch our live event production quality</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-red-500/20 shadow-2xl shadow-red-500/10 bg-[#0a0a0a]">
              {/* Video Player */}
              <div className="relative aspect-video">
                <video
                  ref={videoRef}
                  src={require('../assets/video/designarena_video_85zjlse9.mp4')}
                  muted={videoMuted}
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ filter: 'contrast(1.1) saturate(1.1)' }}
                />
                
                {/* Video Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent opacity-60"></div>
                
                {/* Live Badge */}
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-red-500/90 text-white px-3 py-1.5 rounded-full text-xs font-bold tracking-wider">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span>LIVE PRODUCTION</span>
                </div>

                {/* Enable Sound Overlay - Shows when muted and visible */}
                {videoVisible && videoMuted && !userInteracted && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
                    <button
                      onClick={enableSound}
                      className="group flex flex-col items-center space-y-3 bg-red-500 hover:bg-red-600 text-white px-8 py-5 rounded-2xl transition-all transform hover:scale-105 shadow-2xl shadow-red-500/30"
                    >
                      <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      </div>
                      <div className="text-center">
                        <span className="font-bold text-lg block">Tap to Enable Sound</span>
                        <span className="text-white/70 text-sm">Click to hear the experience</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Video Controls Bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#030303] to-transparent p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  {/* Left: Play/Mute Controls */}
                  <div className="flex items-center space-x-3">
                    {/* Mute/Unmute Button */}
                    <button
                      onClick={handleVideoMute}
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center transition-all backdrop-blur-sm border border-white/10"
                    >
                      {videoMuted ? (
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      )}
                    </button>

                    {/* Volume Label */}
                    <span className="text-white/60 text-xs sm:text-sm hidden sm:block">
                      {videoMuted ? 'Muted' : `Volume: ${Math.round(videoVolume * 100)}%`}
                    </span>
                  </div>

                  {/* Center: Video Title */}
                  <div className="hidden md:block text-center">
                    <span className="text-white/80 text-sm font-medium">Amit Sound & Production</span>
                    <span className="text-white/40 text-xs block">Live Event Coverage</span>
                  </div>

                  {/* Right: Volume Slider */}
                  <div className="flex items-center space-x-3">
                    {/* Volume Icon */}
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                    
                    {/* Volume Slider */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={videoVolume}
                      onChange={handleVolumeChange}
                      className="w-24 sm:w-32 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-red-500 hover:accent-red-400"
                      style={{
                        background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${videoVolume * 100}%, rgba(255,255,255,0.2) ${videoVolume * 100}%, rgba(255,255,255,0.2) 100%)`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Video Info Cards */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              {[{ Icon: Camera, label: '4K Quality', value: 'Crystal Clear' }, { Icon: Volume2, label: 'Pro Audio', value: 'Live Sound' }, { Icon: Zap, label: 'Real-time', value: 'No Lag' }].map((item, i) => (
                <div key={i} className={`rounded-xl p-4 text-center hover:border-red-500/30 transition-colors border ${darkMode ? 'bg-white/[0.02] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                  <div className="text-2xl mb-2 text-red-500 flex justify-center"><item.Icon className="w-6 h-6" /></div>
                  <div className={`text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{item.label}</div>
                  <div className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className={`relative py-12 sm:py-20 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute -top-20 -left-20 w-[600px] h-[600px] rounded-full blur-[160px] ${darkMode ? 'bg-red-500/8' : 'bg-red-500/12'}`}></div>
          <div className={`absolute -bottom-20 -right-20 w-[550px] h-[550px] rounded-full blur-[140px] ${darkMode ? 'bg-red-600/7' : 'bg-red-600/11'}`}></div>
          <div className={`absolute top-1/2 right-1/4 w-[350px] h-[350px] rounded-full blur-[100px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-red-600/5 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center space-x-2 mb-4 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Our Services</span>
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
              WHAT WE <span className="text-red-500">OFFER</span>
            </h2>
            <p className={`max-w-2xl mx-auto text-sm sm:text-base ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
              Complete audio-visual solutions tailored for every event scale. From intimate gatherings to massive stadium shows, we provide end-to-end event production services.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div 
                key={i} 
                onClick={() => setActiveService(i)} 
                className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:transform hover:scale-[1.02] hover:-translate-y-2 ${activeService === i ? 'ring-2 ring-red-500 shadow-2xl shadow-red-500/20' : 'hover:shadow-2xl hover:shadow-red-500/10'}`}
                style={{ perspective: '1000px' }}
              >
                {/* Card Background with Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent transition-all duration-500 ${activeService === i ? 'opacity-100' : 'opacity-50 group-hover:opacity-80'}`}></div>
                
                {/* Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 transition-all duration-500 ${activeService === i ? 'from-red-500/10 to-transparent' : 'group-hover:from-red-500/5 group-hover:to-transparent'}`}></div>
                
                {/* Border */}
                <div className={`absolute inset-0 rounded-2xl border transition-all duration-300 ${activeService === i ? 'border-red-500/50' : (darkMode ? 'border-white/10 group-hover:border-red-500/30' : 'border-gray-200 group-hover:border-red-500/30')}`}></div>

                {/* Image Section */}
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img 
                    src={require(`../assets/images/${service.img}`)} 
                    alt="" 
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/60 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Price Badge */}
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-red-600 to-red-500 text-white text-xs px-4 py-2 rounded-full font-bold shadow-lg shadow-red-500/30 transform group-hover:scale-110 transition-transform duration-300">
                    {service.price}
                  </div>

                  {/* Service Number */}
                  <div className={`absolute bottom-4 right-4 text-5xl font-black transition-colors ${darkMode ? 'text-white/5 group-hover:text-white/10' : 'text-gray-200 group-hover:text-gray-300'}`}>
                    0{i + 1}
                  </div>
                </div>

                {/* Content Section */}
                <div className="relative p-6">
                  {/* Icon and Title */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl flex items-center justify-center border border-red-500/20 group-hover:bg-red-500/30 transition-colors duration-300">
                      <service.Icon className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className={`font-bold text-lg sm:text-xl group-hover:text-red-500 transition-colors ${darkMode ? 'text-white' : 'text-gray-900'}`}>{service.title}</h3>
                      <div className="h-0.5 w-0 bg-red-500 group-hover:w-12 transition-all duration-300 mt-1"></div>
                    </div>
                  </div>

                  {/* Specs with enhanced styling */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {service.specs.map((s, j) => (
                      <span 
                        key={j} 
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-all duration-300 group-hover:border-red-500/20 ${darkMode ? 'bg-white/[0.03] text-white/60 border-white/5 group-hover:bg-red-500/5 group-hover:text-white/80' : 'bg-gray-100 text-gray-600 border-gray-200 group-hover:bg-red-50 group-hover:text-gray-800'}`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button 
                    onClick={(e) => { e.stopPropagation(); scrollToSection('contact'); }} 
                    className="w-full py-3.5 bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/30 rounded-xl text-red-500 text-sm font-bold tracking-wider hover:bg-red-500 hover:text-white hover:border-red-500 hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300 flex items-center justify-center space-x-2 group/btn"
                  >
                    <span>GET QUOTE</span>
                    <svg className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>

                {/* Bottom Accent Line */}
                <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-red-500 to-red-600 transition-all duration-500 ${activeService === i ? 'w-full' : 'w-0 group-hover:w-full'}`}></div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <div className={`inline-flex items-center space-x-2 text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
              <span>Need custom package?</span>
              <button onClick={() => scrollToSection('contact')} className="text-red-500 hover:text-red-400 font-medium underline underline-offset-4 hover:no-underline transition-all">
                Let's discuss your event
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WORK/PROJECTS SECTION */}
      <section id="work" className={`relative py-12 sm:py-20 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-[180px] ${darkMode ? 'bg-red-500/8' : 'bg-red-500/12'}`}></div>
          <div className={`absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] ${darkMode ? 'bg-red-600/6' : 'bg-red-600/10'}`}></div>
          <div className={`absolute top-1/3 left-0 w-[400px] h-[400px] rounded-full blur-[120px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        {/* Logo Watermark */}
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] opacity-[0.02] pointer-events-none">
          <img src={require('../assets/images/logo.jpg')} alt="" className="w-full h-full object-contain grayscale" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-[2px] w-8 bg-red-500"></div>
              <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Portfolio</span>
            </div>
            <h2 className={`text-3xl sm:text-5xl lg:text-6xl font-black ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              FEATURED<br />
              <span className={darkMode ? 'text-white/30' : 'text-gray-400'}>PROJECTS</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {projects.map((project, index) => {
              const isLarge = index === 0 || index === 3;
              return (
                <div key={project.id} onClick={() => openProjectModal(project)} onMouseEnter={() => setActiveProject(index)} onMouseLeave={() => setActiveProject(null)} className={`group relative overflow-hidden rounded-2xl border hover:border-red-500/40 transition-all cursor-pointer ${isLarge ? 'lg:col-span-7' : 'lg:col-span-5'} ${darkMode ? 'border-white/10' : 'border-gray-200'}`} style={{ minHeight: '350px' }}>
                  <img src={require(`../assets/images/${project.img}`)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${darkMode ? 'from-[#030303] via-[#030303]/60 to-transparent' : 'from-gray-900 via-gray-900/60 to-transparent'}`}></div>
                  <div className={`absolute top-4 right-4 text-6xl font-black ${darkMode ? 'text-white/5' : 'text-gray-200/20'}`}>{project.id}</div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.tags.map((tag, i) => (
                        <span key={i} className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`}>{tag}</span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-red-500 text-sm font-bold">{project.stats}</span>
                      <span className={`w-1 h-1 rounded-full ${darkMode ? 'bg-white/30' : 'bg-gray-400'}`}></span>
                      <span className={`text-sm ${darkMode ? 'text-white/50' : 'text-gray-400'}`}>{project.year}</span>
                    </div>
                    <h3 className={`inline-block text-xl sm:text-2xl font-black mb-1 px-3 py-1 rounded-lg ${darkMode ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`}>{project.title}</h3>
                    <p className={`text-sm ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>{project.subtitle}</p>
                    <div className="w-0 h-1 bg-red-500 group-hover:w-full transition-all duration-500 mt-4"></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECT DETAIL MODAL */}
      {projectModalOpen && selectedProject && (
        <div className="fixed inset-0 z-[100] bg-[#030303]/95 backdrop-blur-xl overflow-y-auto" onClick={closeProjectModal}>
          <div className="min-h-screen px-4 py-8 sm:py-12" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button onClick={closeProjectModal} className="fixed top-4 right-4 sm:top-6 sm:right-6 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="max-w-5xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedProject.tags.map((tag, i) => (
                    <span key={i} className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`}>{tag}</span>
                  ))}
                </div>
                <h2 className={`text-3xl sm:text-5xl font-black mb-2 px-4 py-2 rounded-lg inline-block ${darkMode ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`}>{selectedProject.title}</h2>
                <p className={`text-lg ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>{selectedProject.subtitle}</p>
              </div>

              {/* Hero Image */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-8 aspect-video">
                <img src={require(`../assets/images/${selectedProject.img}`)} alt={selectedProject.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 flex items-center space-x-4">
                  <div className="bg-red-500/90 text-white px-4 py-2 rounded-full text-sm font-bold">{selectedProject.stats} Events</div>
                  <div className="text-white/70 text-sm">{selectedProject.year}</div>
                </div>
              </div>

              {/* Content Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Challenge */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                  <h3 className="text-red-500 font-bold mb-3 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <span>The Challenge</span>
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{selectedProject.details.challenge}</p>
                </div>

                {/* Solution */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                  <h3 className="text-red-500 font-bold mb-3 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>Our Solution</span>
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{selectedProject.details.solution}</p>
                </div>
              </div>

              {/* Equipment Used */}
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-8">
                <h3 className="text-red-500 font-bold mb-4 flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                  <span>Equipment Deployed</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.details.equipment.map((item, i) => (
                    <span key={i} className="text-sm bg-red-500/10 text-white/70 px-3 py-2 rounded-lg border border-red-500/20">{item}</span>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="bg-gradient-to-r from-red-900/20 to-transparent border border-red-500/20 rounded-2xl p-6 mb-8">
                <h3 className="text-red-500 font-bold mb-3 flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  <span>Results & Impact</span>
                </h3>
                <p className="text-white/70 leading-relaxed">{selectedProject.details.results}</p>
              </div>

              {/* Testimonial */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <svg className="w-8 h-8 text-red-500/30 mb-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
                <p className="text-white/80 italic text-lg mb-4 leading-relaxed">"{selectedProject.details.testimonial.split(' - ')[0]}"</p>
                <p className="text-red-400 text-sm font-medium">- {selectedProject.details.testimonial.split(' - ')[1]}</p>
              </div>

              {/* CTA */}
              <div className="mt-8 text-center">
                <button onClick={() => { closeProjectModal(); scrollToSection('contact'); }} className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center space-x-2">
                  <span>Discuss Your Event</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GALLERY SECTION */}
      <section id="gallery" className={`relative py-12 sm:py-20 ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 left-0 w-[600px] h-[600px] rounded-full blur-[160px] ${darkMode ? 'bg-red-600/8' : 'bg-red-600/12'}`}></div>
          <div className={`absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] ${darkMode ? 'bg-red-500/6' : 'bg-red-500/10'}`}></div>
          <div className={`absolute top-1/2 right-1/4 w-[350px] h-[350px] rounded-full blur-[100px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Gallery</span>
            <h2 className={`text-3xl sm:text-5xl font-black mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>VISUAL JOURNEY</h2>
            <p className={`mt-3 text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Click any image to view in fullscreen</p>
          </div>

          <div className="sm:hidden -mx-4 px-4">
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-4">
              {allImages.slice(0, 8).map((img, i) => (
                <div key={i} onClick={() => openLightbox(i)} className="flex-shrink-0 w-[280px] snap-center relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer">
                  <img src={require(`../assets/images/${img.img}`)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <div className="text-red-500 text-xs">{img.cat}</div>
                    <div className="font-bold text-sm">{img.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {allImages.map((img, i) => (
              <div key={i} onClick={() => openLightbox(i)} className={`group relative aspect-square rounded-xl overflow-hidden border hover:border-red-500/30 transition-all cursor-pointer ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
                <img src={require(`../assets/images/${img.img}`)} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent opacity-80"></div>
                <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/10 transition-colors"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-red-500 text-xs">{img.cat}</div>
                  <div className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{img.title}</div>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 bg-white/10 backdrop-blur rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* LIGHTBOX MODAL */}
        {lightboxOpen && (
          <div className="fixed inset-0 z-[100] bg-[#030303]/98 backdrop-blur-xl flex items-center justify-center" onClick={closeLightbox}>
            {/* Close Button */}
            <button onClick={closeLightbox} className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10 px-4 py-2 bg-white/10 rounded-full">
              <span className="text-red-500 font-bold">{lightboxIndex + 1}</span>
              <span className="text-white/40 mx-2">/</span>
              <span className="text-white/60">{allImages.length}</span>
            </div>

            {/* Navigation Arrows */}
            <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-2 sm:left-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-2 sm:right-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>

            {/* Main Image Container */}
            <div className="relative w-full h-full flex flex-col items-center justify-center px-16 sm:px-24 py-16" onClick={(e) => e.stopPropagation()}>
              <div className="relative max-w-5xl max-h-[70vh] sm:max-h-[75vh]">
                <img 
                  src={require(`../assets/images/${allImages[lightboxIndex].img}`)} 
                  alt={allImages[lightboxIndex].title}
                  className="max-w-full max-h-[70vh] sm:max-h-[75vh] object-contain rounded-xl shadow-2xl shadow-red-500/20"
                />
              </div>

              {/* Caption */}
              <div className="mt-4 sm:mt-6 text-center">
                <div className="inline-flex items-center space-x-3 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                  <span className="text-red-500 text-xs tracking-wider uppercase">{allImages[lightboxIndex].cat}</span>
                  <span className="w-1 h-1 bg-white/30 rounded-full"></span>
                  <span className="text-white font-bold text-sm sm:text-base">{allImages[lightboxIndex].title}</span>
                </div>
              </div>
            </div>

            {/* Thumbnail Strip */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 px-4 overflow-x-auto">
              {allImages.map((img, i) => (
                <button 
                  key={i} 
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                  className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${i === lightboxIndex ? 'border-red-500 scale-110' : 'border-white/20 opacity-50 hover:opacity-100'}`}
                >
                  <img src={require(`../assets/images/${img.img}`)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* BEAUTIFUL VENUES SECTION */}
      <section id="venues" className={`relative py-12 sm:py-20 overflow-hidden ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[150px] ${darkMode ? 'bg-red-500/8' : 'bg-red-500/12'}`}></div>
          <div className={`absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] ${darkMode ? 'bg-red-600/6' : 'bg-red-600/10'}`}></div>
          <div className={`absolute top-0 right-0 w-[300px] h-[300px] rounded-full blur-[90px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-red-600/5 rounded-full blur-[80px]"></div>
        </div>

        {/* Logo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-[0.015] pointer-events-none">
          <img src={require('../assets/images/logo.jpg')} alt="" className="w-full h-full object-contain grayscale" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <span className="text-red-500 text-xs tracking-[0.2em] uppercase">Our Reach</span>
            <h2 className={`text-3xl sm:text-5xl font-black mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>VENUES WE COVER</h2>
            <p className={`mt-4 max-w-lg mx-auto ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Serving major cities across Gujarat with premium event production</p>
          </div>

          {/* Beautiful Venue Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {venues.map((v, i) => (
              <div key={i} onMouseEnter={() => setActiveVenue(v)} onMouseLeave={() => setActiveVenue(null)} className={`group relative bg-gradient-to-br border rounded-2xl p-5 transition-all cursor-pointer ${activeVenue?.name === v.name ? 'border-red-500/50 bg-red-500/5' : (darkMode ? 'bg-[#030303]' : 'bg-gray-50')}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="text-3xl text-red-500"><v.Icon className="w-8 h-8" /></div>
                  <div className="text-right">
                    <div className="text-red-500 font-black text-xl">{v.events}</div>
                    <div className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-500'}`}>Events</div>
                  </div>
                </div>
                <h3 className={`font-bold text-lg mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{v.name}</h3>
                <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>{v.desc}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-red-500 group-hover:w-full transition-all duration-500 rounded-b-2xl"></div>
              </div>
            ))}
          </div>

          {/* Connection Lines Visual */}
          <div className="hidden lg:block relative mt-12 h-40">
            <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-red-500/20 to-transparent"></div>
            <div className="flex justify-center items-start space-x-16 px-8">
              {venues.slice(0, 5).map((v, i) => (
                <div key={i} className="relative flex flex-col items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <div className={`mt-4 text-xs text-center w-20 leading-tight ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{v.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL CONTACT SECTION */}
      <section id="contact" className={`relative pt-10 pb-16 sm:pt-12 sm:pb-24 ${darkMode ? 'bg-[#030303]' : 'bg-gray-50'}`}>
        {/* Enhanced Red Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-[180px] ${darkMode ? 'bg-red-500/8' : 'bg-red-500/12'}`}></div>
          <div className={`absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[140px] ${darkMode ? 'bg-red-600/6' : 'bg-red-600/10'}`}></div>
          <div className={`absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full blur-[120px] ${darkMode ? 'bg-red-400/5' : 'bg-red-400/8'}`}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-red-500 text-xs tracking-[0.3em] uppercase font-medium">Contact</span>
            <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-black mt-3 mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Ready to <span className="text-red-500">Amplify</span>?
            </h2>
            <p className={`max-w-2xl mx-auto text-base ${darkMode ? 'text-white/50' : 'text-gray-600'}`}>
              Planning an event that demands flawless sound? Let Amit Sound elevate your experience with professional audio solutions. Contact us today and let's make your event unforgettable!
            </p>
          </div>

          {/* Contact Options - No Form, Direct Actions */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Call */}
            <a href="tel:9624569200" className={`group border rounded-2xl p-8 text-center transition-all ${darkMode ? 'bg-[#111] border-white/10 hover:border-red-500/50 hover:bg-[#161616]' : 'bg-white border-gray-200 hover:border-red-500 hover:bg-gray-50'}`}>
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-500/20 transition-colors">
                <Phone className="w-7 h-7 text-red-500" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Call Us</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Speak directly with our team</p>
              <span className="text-red-500 font-semibold">9624569200</span>
            </a>

            {/* WhatsApp */}
            <a href="https://wa.me/919624569200?text=Hi%20Amit%20Sound%2C%20I'm%20interested%20in%20your%20event%20production%20services.%20Can%20we%20discuss%20my%20requirements%3F" target="_blank" rel="noopener noreferrer" className={`group border rounded-2xl p-8 text-center transition-all ${darkMode ? 'bg-[#111] border-white/10 hover:border-green-500/50 hover:bg-[#161616]' : 'bg-white border-gray-200 hover:border-green-500 hover:bg-gray-50'}`}>
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/20 transition-colors">
                <svg className="w-7 h-7 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </div>
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>WhatsApp</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Quick chat on WhatsApp</p>
              <span className="text-green-500 font-semibold">+91 9624569200</span>
            </a>

            {/* Email */}
            <a href="mailto:info@amitsound.com" target="_blank" rel="noopener noreferrer" className={`group border rounded-2xl p-8 text-center transition-all ${darkMode ? 'bg-[#111] border-white/10 hover:border-blue-500/50 hover:bg-[#161616]' : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-gray-50'}`}>
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/20 transition-colors">
                <Mail className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Email</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Send us an email</p>
              <span className="text-blue-500 font-semibold text-sm">info@amitsound.com</span>
            </a>
          </div>

          {/* Contact Details */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
            <div>
              <p className={`text-xs uppercase tracking-wider mb-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Phone</p>
              <p className={darkMode ? 'text-white' : 'text-gray-900'}>9624569200</p>
              <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>9898269200</p>
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wider mb-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Email</p>
              <p className={darkMode ? 'text-white' : 'text-gray-900'}>info@amitsound.com</p>
              <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>amitsound@gmail.com</p>
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wider mb-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Office</p>
              <p className={darkMode ? 'text-white' : 'text-gray-900'}>26, Sumeru Complex</p>
              <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Viramgam, Gujarat</p>
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wider mb-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Hours</p>
              <p className={darkMode ? 'text-white' : 'text-gray-900'}>24 Hours / 7 Days</p>
              <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>Always Open</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={`border-t pt-12 pb-8 ${darkMode ? 'bg-[#030303] border-red-500/10' : 'bg-gray-50 border-gray-200'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <div className="relative">
                <img src={require('../assets/images/logo.jpg')} alt="Amit Sound" className="w-12 h-12 rounded-lg border border-red-500/30" />
                <div>
                  <span className={`text-xl font-black tracking-wider ${darkMode ? 'text-white' : 'text-gray-900'}`}>AMIT</span>
                  <span className="text-xl font-black tracking-wider text-red-500">SOUND</span>
                </div>
              </div>
              <p className={`text-sm leading-relaxed mb-6 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
                Gujarat's premier sound & lighting production company delivering exceptional audio experiences since <span className="text-red-500">1985</span>.
              </p>
              {/* Social Links */}
              <div className="flex space-x-3">
                <a href="https://www.facebook.com/amitsound.viramgam/" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all group ${darkMode ? 'bg-white/5 border border-white/10 hover:bg-red-500 hover:border-red-500' : 'bg-gray-200 border border-gray-300 hover:bg-red-500 hover:border-red-500'}`}>
                  <svg className={`w-5 h-5 group-hover:text-white transition-colors ${darkMode ? 'text-white/60' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/amit_sound_viramgam" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all group ${darkMode ? 'bg-white/5 border border-white/10 hover:bg-red-500 hover:border-red-500' : 'bg-gray-200 border border-gray-300 hover:bg-red-500 hover:border-red-500'}`}>
                  <svg className={`w-5 h-5 group-hover:text-white transition-colors ${darkMode ? 'text-white/60' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://youtube.com/amitsound" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all group ${darkMode ? 'bg-white/5 border border-white/10 hover:bg-red-500 hover:border-red-500' : 'bg-gray-200 border border-gray-300 hover:bg-red-500 hover:border-red-500'}`}>
                  <svg className={`w-5 h-5 group-hover:text-white transition-colors ${darkMode ? 'text-white/60' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className={`font-bold mb-5 text-sm tracking-wider uppercase ${darkMode ? 'text-white' : 'text-gray-900'}`}>Quick Links</h4>
              <ul className="space-y-3">
                {['Home', 'About Us', 'Services', 'Our Work', 'Gallery', 'Contact'].map((link) => (
                  <li key={link}>
                    <button 
                      onClick={() => scrollToSection(link.toLowerCase().replace(' us', '').replace(' our', ''))} 
                      className={`text-sm transition-colors flex items-center space-x-2 group ${darkMode ? 'text-white/40 hover:text-red-500' : 'text-gray-600 hover:text-red-500'}`}
                    >
                      <span className="w-0 h-px bg-red-500 group-hover:w-3 transition-all"></span>
                      <span>{link}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className={`font-bold mb-5 text-sm tracking-wider uppercase ${darkMode ? 'text-white' : 'text-gray-900'}`}>Our Services</h4>
              <ul className="space-y-3">
                {[
                  { name: 'Sound Systems', icon: Volume2 },
                  { name: 'Stage Lighting', icon: Lightbulb },
                  { name: 'LED Video Walls', icon: Monitor },
                  { name: 'Live Streaming', icon: Radio },
                  { name: 'DJ & Stage Setup', icon: Headphones }
                ].map((service) => (
                  <li key={service.name}>
                    <span className={`text-sm flex items-center space-x-2 transition-colors cursor-pointer ${darkMode ? 'text-white/40 hover:text-red-500' : 'text-gray-600 hover:text-red-500'}`}>
                      <service.icon className="w-4 h-4" />
                      <span>{service.name}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className={`font-bold mb-5 text-sm tracking-wider uppercase ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:9624569200" className={`flex items-start space-x-3 transition-colors group ${darkMode ? 'text-white/40 hover:text-red-500' : 'text-gray-600 hover:text-red-500'}`}>
                    <Phone className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>9624569200</p>
                      <p className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-500'}`}>9898269200</p>
                    </div>
                  </a>
                </li>
                <li>
                  <a href="mailto:info@amitsound.com" className={`flex items-start space-x-3 transition-colors ${darkMode ? 'text-white/40 hover:text-red-500' : 'text-gray-600 hover:text-red-500'}`}>
                    <Mail className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>info@amitsound.com</p>
                      <p className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-500'}`}>amitsound@gmail.com</p>
                    </div>
                  </a>
                </li>
                <li>
                  <div className={`flex items-start space-x-3 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
                    <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>26, Sumeru Complex</p>
                      <p className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-500'}`}>Viramgam, Gujarat 382150</p>
                    </div>
                  </div>
                </li>
                <li>
                  <div className={`flex items-center space-x-3 ${darkMode ? 'text-white/40' : 'text-gray-600'}`}>
                    <Clock className="w-5 h-5 text-red-500" />
                    <span className="text-sm">24/7 Available</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`pt-8 ${darkMode ? 'border-t border-white/5' : 'border-t border-gray-300'}`}>
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className={`text-sm ${darkMode ? 'text-white/30' : 'text-gray-500'}`}>
                © {new Date().getFullYear()} Amit Sound & Production. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className={`text-sm transition-colors ${darkMode ? 'text-white/30 hover:text-red-500' : 'text-gray-500 hover:text-red-500'}`}>Privacy Policy</a>
                <a href="#" className={`text-sm transition-colors ${darkMode ? 'text-white/30 hover:text-red-500' : 'text-gray-500 hover:text-red-500'}`}>Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes waveform {
          0% { transform: scaleY(0.3); }
          100% { transform: scaleY(1); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-25px); }
        }
        @keyframes wave1 {
          0%, 100% { transform: translate(-25%, -25%) rotate(0deg); }
          50% { transform: translate(-25%, -25%) rotate(180deg); }
        }
        @keyframes wave2 {
          0%, 100% { transform: translate(-25%, -25%) rotate(0deg) scale(1); }
          50% { transform: translate(-25%, -25%) rotate(-180deg) scale(1.1); }
        }
        @keyframes wave3 {
          0%, 100% { transform: translate(0%, 0%) scale(1); }
          50% { transform: translate(-10%, -10%) scale(1.2); }
        }
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 6s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 5s ease-in-out infinite; }
        .snap-x { scroll-snap-type: x mandatory; }
        .snap-center { scroll-snap-align: center; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #030303; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default Home;
